/**
 * Module card component with 360-degree preview and drag support
 */

import { useState, useEffect, useRef, useCallback } from "react";
import { ActionIcon, Card, Text, Badge, Box, Tooltip, Menu, Group } from "@mantine/core";
import { IconTrash } from "@tabler/icons-react";
import { useDrag } from "react-dnd";
import type { ModuleData } from "../types";
import { useAssetsBaseUrl, usePendingPlacementModule, useViewerStore } from "../store";
import { useIsMobile } from "../hooks";

interface ModuleCardProps {
  module: ModuleData;
}

export const MODULE_DRAG_TYPE = "module";

const getCategoryColor = (category: string): string => {
  const colors: Record<string, string> = {
    bases: "green",
    arms: "blue",
    grippers: "orange",
    sensors: "grape",
    connectors: "cyan",
    others: "gray",
  };
  return colors[category] || "gray";
};

const getServoSeriesColor = (servoSeries: string): string => {
  const colors: Record<string, string> = {
    kondo: "red",
    dynamixel: "indigo",
    can: "teal",
  };
  return colors[servoSeries] || "yellow";
};

function Module360Preview({ module }: { module: ModuleData }) {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const assetsBaseUrl = useAssetsBaseUrl();

  // Get video URL - prefer video_url from server, otherwise construct from assets base
  const getVideoUrl = (): string | null => {
    if (module.video_url) {
      return module.video_url;
    }
    // Fallback: construct URL based on naming convention
    return `${assetsBaseUrl}/360_images/${module.name}/${module.name}_360.mp4`;
  };

  const videoUrl = getVideoUrl();

  // Handle hover to play/pause video
  useEffect(() => {
    if (!videoRef.current || hasError) return;

    if (isHovering) {
      videoRef.current.play().catch(() => {
        // Ignore autoplay errors
      });
    } else {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  }, [isHovering, hasError]);

  // Check if video is available
  useEffect(() => {
    if (!videoUrl) {
      setIsLoading(false);
      setHasError(true);
      return;
    }

    // Create a test video element to check if the video loads
    const testVideo = document.createElement("video");
    testVideo.src = videoUrl;
    testVideo.onloadeddata = () => {
      setIsLoading(false);
      setHasError(false);
    };
    testVideo.onerror = () => {
      setIsLoading(false);
      setHasError(true);
    };

    return () => {
      testVideo.src = "";
    };
  }, [videoUrl]);

  // A printed part has a rendered picture but no turntable video: show the picture.
  if ((hasError || !videoUrl) && module.poster_url) {
    return (
      <Box
        style={{
          width: "100%",
          height: "100%",
          overflow: "hidden",
          borderRadius: "4px",
          backgroundColor: "#f8f9fa",
          border: "1px solid #e9ecef",
          minHeight: 100,
        }}
      >
        <img
          src={module.poster_url}
          alt={module.name}
          style={{ width: "100%", height: "100%", objectFit: "contain", pointerEvents: "none" }}
        />
      </Box>
    );
  }

  // Fallback view
  if (hasError || isLoading || !videoUrl) {
    return (
      <Box
        style={{
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#f8f9fa",
          border: "1px solid #e9ecef",
          borderRadius: "4px",
          minHeight: 100,
        }}
      >
        <Box
          style={{
            fontSize: 36,
            color: `var(--mantine-color-${getCategoryColor(module.category)}-6)`,
            opacity: isLoading ? 0.5 : 1.0,
            marginBottom: "8px",
          }}
        >
          📦
        </Box>
        <Text
          size="xs"
          c="dimmed"
          ta="center"
          style={{
            wordBreak: "break-word",
            padding: "0 8px",
          }}
        >
          {isLoading ? "Loading..." : module.name}
        </Text>
      </Box>
    );
  }

  return (
    <Box
      style={{
        width: "100%",
        height: "100%",
        position: "relative",
        overflow: "hidden",
        borderRadius: "4px",
        cursor: "grab",
        minHeight: 100,
      }}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      <video
        ref={videoRef}
        src={videoUrl}
        muted
        loop
        playsInline
        // The poster paints instantly; the browser throttles loading when
        // dozens of video elements mount at once, so without it the library
        // cards sit blank while each video's metadata trickles in.
        poster={module.poster_url ?? undefined}
        preload={module.poster_url ? "none" : "metadata"}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "contain",
          backgroundColor: "#f8f9fa",
          pointerEvents: "none",
        }}
      />
      {/* Hover indicator */}
      {isHovering && (
        <Box
          style={{
            position: "absolute",
            bottom: 4,
            left: "50%",
            transform: "translateX(-50%)",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            color: "white",
            padding: "2px 8px",
            borderRadius: "12px",
            fontSize: 10,
            whiteSpace: "nowrap",
          }}
        >
          360° View
        </Box>
      )}
    </Box>
  );
}

export function ModuleCard({ module }: ModuleCardProps) {
  const [{ isDragging }, drag] = useDrag(() => ({
    type: MODULE_DRAG_TYPE,
    item: { module },
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging(),
    }),
  }), [module]);

  const [menuOpened, setMenuOpened] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const isMobile = useIsMobile();
  const pendingPlacementModule = usePendingPlacementModule();
  const setPendingPlacementModule = useViewerStore((state) => state.setPendingPlacementModule);
  const isArmed = pendingPlacementModule?.id === module.id;

  // Touch has no drag-and-drop to the viewport, so a tap arms the module and
  // the next tap in the scene says where it lands.
  const handleArm = useCallback(() => {
    setPendingPlacementModule(isArmed ? null : module);
  }, [isArmed, module, setPendingPlacementModule]);

  // Show delete menu for any module with owners (server enforces actual ownership)
  const isUserOwned = Boolean(
    module.owner_user_ids && module.owner_user_ids.length > 0
  );

  const handleContextMenu = useCallback(
    (e: React.MouseEvent) => {
      if (!isUserOwned) return;
      e.preventDefault();
      setMenuOpened(true);
    },
    [isUserOwned]
  );

  const handleDelete = useCallback(async () => {
    setDeleting(true);
    try {
      const response = await fetch("/api/delete-module", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ module_id: module.id, session_id: window.sessionId }),
      });
      const result = await response.json();
      if (!result.success) {
        console.error("Failed to delete module:", result.error);
      }
    } catch (err) {
      console.error("Failed to delete module:", err);
    } finally {
      setDeleting(false);
      setMenuOpened(false);
    }
  }, [module.id]);

  const cardContent = (
    <div
      ref={drag}
      style={{ opacity: isDragging || deleting ? 0.5 : 1, position: "relative" }}
      onContextMenu={handleContextMenu}
    >
      <Card
        shadow="sm"
        padding="xs"
        radius="md"
        withBorder
        onClick={isMobile ? handleArm : undefined}
        style={{
          cursor: isMobile ? "pointer" : isDragging ? "grabbing" : "grab",
          transition: "transform 0.2s, box-shadow 0.2s",
          borderColor: isArmed ? "var(--mantine-color-blue-6)" : undefined,
          borderWidth: isArmed ? 2 : undefined,
        }}
        onMouseEnter={(e) => {
          if (!isDragging) {
            e.currentTarget.style.transform = "translateY(-2px)";
            e.currentTarget.style.boxShadow = "var(--mantine-shadow-md)";
          }
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = "translateY(0)";
          e.currentTarget.style.boxShadow = "var(--mantine-shadow-sm)";
        }}
      >
        <Card.Section>
          <Module360Preview module={module} />
        </Card.Section>

        <Tooltip label={module.name} position="bottom" withArrow>
          <Text
            size="xs"
            fw={500}
            mt="xs"
            lineClamp={1}
            style={{ wordBreak: "break-all" }}
          >
            {module.name}
          </Text>
        </Tooltip>

        <Group gap={4} mt={4}>
          <Badge color={getCategoryColor(module.category)} size="xs" variant="light">
            {module.category}
          </Badge>
          {module.servo_series && (
            <Tooltip
              label={`Servos of the same series can share one bus; mixing ${module.servo_series} with a different series won't communicate`}
              position="bottom"
              withArrow
            >
              <Badge color={getServoSeriesColor(module.servo_series)} size="xs" variant="filled">
                {module.servo_series}
              </Badge>
            </Tooltip>
          )}
        </Group>
      </Card>

      {/* Touch has no right-click, so owned modules get an explicit handle
          that opens the same menu the context menu does. */}
      {isMobile && isUserOwned && (
        <ActionIcon
          variant="filled"
          color="gray"
          size="md"
          radius="xl"
          aria-label={`Actions for ${module.name}`}
          style={{ position: "absolute", top: 4, right: 4 }}
          onClick={(event) => {
            event.stopPropagation();
            setMenuOpened(true);
          }}
        >
          <IconTrash size={14} />
        </ActionIcon>
      )}
    </div>
  );

  if (!isUserOwned) return cardContent;

  return (
    <Menu
      opened={menuOpened}
      onClose={() => setMenuOpened(false)}
      position="bottom-start"
      withArrow
    >
      <Menu.Target>
        {cardContent}
      </Menu.Target>

      <Menu.Dropdown>
        <Menu.Item
          color="red"
          leftSection={<IconTrash size={14} />}
          onClick={handleDelete}
        >
          Delete
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
}
